import { useEffect, useRef, useState } from 'react';

import { HEADER_ANIMATION_DELAY_MS } from '../lib/constants';

export const useStepperScroll = (mainContentRef, activeSubstepCode) => {
  const [shouldAnimateHeader, setShouldAnimateHeader] = useState(false);
  const isScrolledRef = useRef(false);
  const hasBeenTriggeredRef = useRef(false);

  useEffect(() => {
    const mainElement = mainContentRef.current;
    if (!mainElement) return;

    mainElement.scrollTop = 0;
    setShouldAnimateHeader(false);
    isScrolledRef.current = false;
    hasBeenTriggeredRef.current = false;

    const scrollDownThreshold = 1;
    const scrollUpThreshold = 0;
    let timeoutId = null;

    const checkIfScrollable = () => mainElement.scrollHeight > mainElement.clientHeight;

    const revertHeader = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      hasBeenTriggeredRef.current = false;
      isScrolledRef.current = false;
      setShouldAnimateHeader(false);
    };

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;

      if (hasBeenTriggeredRef.current && checkIfScrollable() && scrollTop === 0) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        revertHeader();
        return;
      }

      let shouldBeScrolled;
      if (hasBeenTriggeredRef.current) {
        shouldBeScrolled = true;
      } else if (isScrolledRef.current) {
        shouldBeScrolled = scrollTop > scrollUpThreshold;
      } else {
        shouldBeScrolled = scrollTop > scrollDownThreshold;
        if (shouldBeScrolled) {
          hasBeenTriggeredRef.current = true;
        }
      }

      if (shouldBeScrolled !== isScrolledRef.current) {
        isScrolledRef.current = shouldBeScrolled;

        if (!timeoutId) {
          timeoutId = setTimeout(() => {
            setShouldAnimateHeader(shouldBeScrolled);
            timeoutId = null;
          }, HEADER_ANIMATION_DELAY_MS);
        }
      }
    };

    const handleWheel = (e) => {
      if (hasBeenTriggeredRef.current && !checkIfScrollable() && e.deltaY < 0) {
        revertHeader();
      }
    };

    mainElement.addEventListener('scroll', handleScroll, { passive: true });
    mainElement.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      mainElement.removeEventListener('scroll', handleScroll);
      mainElement.removeEventListener('wheel', handleWheel);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeSubstepCode, mainContentRef]);

  return { shouldAnimateHeader };
};
